const asArray = (value) => Array.isArray(value) ? value : [];

export const normaliseColorCode = (value) => String(value ?? "")
  .trim()
  .toUpperCase();

// Detail endpoints expose the summary as an array. Keep the alternate keys
// here to tolerate deployments that wrap the array while the API rolls out.
export const getColorAreaGroups = (source = {}) => {
    const summary = source?.colorAreaSummary;
    const groups = Array.isArray(summary)
        ? summary
        : summary?.groups || summary?.colors || summary?.items || [];

    return asArray(groups).map((group) => ({
        ...group,
        colorCode: normaliseColorCode(group?.colorCode ?? group?.ralColorCode ?? group?.color),
                knownAreaSquareMetres: group?.knownAreaSquareMetres ?? group?.surfaceArea ??
            group?.totalSurfaceArea ?? group?.totalPowderArea ?? null,
        chargeAmount: group?.chargeAmount ?? group?.amount ?? 0,
        // An empty code is intentionally retained as an unassigned review
        // row; it must never be charged to an arbitrary colour.
        isChargeable: Boolean(normaliseColorCode(group?.colorCode ?? group?.ralColorCode ?? group?.color)),
    }));
};

export const getColorChargeRows = (source = {}) =>
    asArray(source?.colorCharges).map((charge) => ({
        ...charge,
        colorCode: normaliseColorCode(charge?.colorCode ?? charge?.ralColorCode),
        chargeAmount: charge?.chargeAmount ?? charge?.amount ?? 0,
    })).filter((charge) => charge.colorCode);

export const buildColorChargeRows = (source = {}) => {
    const groups = getColorAreaGroups(source);
    const chargesByColor = new Map(
        getColorChargeRows(source).map((charge) => [String(charge.colorCode), charge])
    );

    const groupedRows = groups.map((group) => ({
        ...group,
        ...(chargesByColor.get(String(group.colorCode)) || {}),
        colorCode: group.colorCode,
        // `colorAreaSummary.chargeAmount` is the area-calculation/suggested
        // amount. The commercial summary is based on the approved quotation
        // charge, so show that same value in the special-colour drawer.
        chargeAmount: chargesByColor.get(String(group.colorCode))?.chargeAmount ?? group?.chargeAmount ?? 0,
    }));

  return groupedRows;
};

export const sumColorChargeAmounts = (rows = []) =>
    rows.reduce((total, row) => total + (Number(row?.chargeAmount) || 0), 0);

// Sales orders and factory releases store the approved charge in their
// transaction currency as `amount`; quotation rows name it `chargeAmount`.
export const toTransactionColorCharges = (rows) =>
  asArray(rows)
    .filter((row) => row?.colorCode)
    .map((row) => ({
      colorCode: normaliseColorCode(row.colorCode),
      amount: Number(row?.amount ?? row?.chargeAmount) || 0,
    }));

export const toRemainingTransactionColorCharges = (rows) =>
  asArray(rows)
    .filter((row) => row?.colorCode)
    .map((row) => ({
      colorCode: normaliseColorCode(row.colorCode),
      amount: Number(
        row?.remainingAmount ?? row?.amount ?? row?.chargeAmount
      ) || 0,
    }))
    .filter((row) => row.amount > 0);

const resolvedProductColor = (product) => {
  const directColor =
    product?.colorCode ?? product?.ralColorCode ?? product?.resolvedColorCode ??
    product?.constructionColorCode ?? product?.faiConstructionColorCode;
  if (directColor) return directColor;

  const specifications = product?.productSpecifications ?? product?.specifications ?? [];
  const colorSpecification = asArray(specifications).find(
    (specification) => Number(specification?.specificationId ?? specification?.attributeId) === 4
  );

  return colorSpecification?.colorCode ?? colorSpecification?.ralColorCode ??
    colorSpecification?.resolvedColorCode ?? colorSpecification?.specialValue ??
    colorSpecification?.SpecialValue ??
    colorSpecification?.valueName ?? colorSpecification?.attributeValue ?? "";
};

export const synchroniseQuotationColorCharges = (quotation = {}) => {
  const currentColors = new Set(
    getColorAreaGroups(quotation)
      .map((group) => normaliseColorCode(group?.colorCode))
      .filter(Boolean)
  );
  const retainCurrentColor = (row) => currentColors.size === 0 || currentColors.has(
    normaliseColorCode(row?.colorCode ?? row?.ralColorCode ?? row?.color)
  );

  const distinctRowsByColor = (rows) => Array.from(
    rows.reduce((byColor, row) => {
      const rawColorCode = row?.colorCode ?? row?.ralColorCode ?? row?.color;
      const colorCode = normaliseColorCode(rawColorCode);
      const isCanonicalCode = String(rawColorCode ?? "").trim().toUpperCase() === colorCode;
      const existing = byColor.get(colorCode);

      // Preserve the first approved allocation for an identical saved code.
      if (colorCode && (!existing || (!existing.isCanonicalCode && isCanonicalCode))) {
        byColor.set(colorCode, { row: { ...row, colorCode }, isCanonicalCode });
      }
      return byColor;
    }, new Map()).values()
  ).map(({ row }) => row);

  const colorAreaSummary = Array.isArray(quotation?.colorAreaSummary)
    ? distinctRowsByColor(quotation.colorAreaSummary.filter(retainCurrentColor))
    : quotation?.colorAreaSummary;
  const colorCharges = Array.isArray(quotation?.colorCharges)
    ? distinctRowsByColor(quotation.colorCharges.filter(retainCurrentColor))
    : quotation?.colorCharges;

  return {
    ...quotation,
    colorAreaSummary,
    colorCharges,
  };
};


export const toReleasedProductColorCharges = (rows, releasedProducts) => {
  const releasedColors = new Set(
    asArray(releasedProducts)
      .map(resolvedProductColor)
      .map(normaliseColorCode)
      .filter(Boolean)
  );

  if (releasedColors.size === 0) return [];

  return toRemainingTransactionColorCharges(rows).filter((charge) =>
    releasedColors.has(normaliseColorCode(charge.colorCode))
  );
};
